import { observable, computed, makeObservable, action, toJS } from 'mobx';
import { AcBuildURLSearchParams } from '@utils';
import { acMapPublication } from '@src/utilities/ac-map-publication';

let app = {};

const LIMIT = 7;

export const DEFAULT_SEARCH_QUERY = {
  _limit: LIMIT,
};

const DEFAULT_QUERY = {};

if (process.env.API_URL_COMMONGROUND_ORGANIZATION_OIN) {
}

// Facetstructuur van de nieuwe API: facets.<key>.data.buckets met
// {value, label, count}; componenten verwachten {key, results}.
const acNormalizeBuckets = (facet) =>
  facet?.data?.buckets?.map((bucket) => ({
    key: bucket.label,
    value: bucket.value,
    results: bucket.count,
  })) || [];

export class PublicationsStore {
  constructor(store) {
    makeObservable(this);
    app.store = store;
  }

  @observable
  mobileFiltersOpen = false;

  @observable
  items = [];

  @observable
  latest_items = [];

  @observable
  single = null;

  @observable
  attachments = null;

  @observable
  categories = [];

  @observable
  categoryFacets = [];

  @observable
  themes = [];

  @observable
  themesFacets = [];

  @observable
  pagination = {};

  @observable
  attachmentPagination = {
    page: 1,
    perPage: 10,
  };

  @observable
  defaultQuery = DEFAULT_QUERY;

  @observable
  aggregationsQuery = {
    _facetable: 'true',
    '_facets[@self][schema][type]': 'terms',
    '_facets[thema][type]': 'terms',
  };

  @observable
  query = DEFAULT_SEARCH_QUERY;

  @observable
  loading = {
    status: false,
    message: undefined,
  };

  @observable
  loading_latest = {
    status: false,
    message: undefined,
  };

  @observable
  attachmentSearch = '';

  @computed
  get all_themes() {
    return this.themes;
  }

  @computed
  get all_categories() {
    return this.categories;
  }

  @computed
  get all_category_facets() {
    return this.categoryFacets;
  }

  @computed
  get categories_with_facets() {
    return this.categories.map((category) => {
      delete category.results;
      const facet = this.categoryFacets.find((facet) => facet.key === category.key);
      return {
        ...category,
        results: facet?.results || 0,
      };
    });
  }

  @action
  getFilteredAttachments = (primary = false, page) => {
    // Filter by both document type (primary/secondary) and search term
    const filteredAttachmentsLabel = this.single?.attachments?.filter(
      (attachment) => {
        // First filter by primary/secondary status using labels
        const isPrimary = attachment?.labels?.length > 0;

        // Then also filter by search term if one exists
        const matchesSearch =
          !this.attachmentSearch ||
          attachment?.title
            ?.toLowerCase()
            .includes(this.attachmentSearch.toLowerCase());

        return (primary ? isPrimary : !isPrimary) && matchesSearch;
      }
    );

    const filteredAttachments = [];
    filteredAttachmentsLabel &&
      filteredAttachmentsLabel.forEach((attachment) => {
        for (let i = 1; i <= attachment.labels?.length; i++) {
          filteredAttachments.push({
            ...attachment,
            labels: [attachment.labels[i - 1]],
          });
        }
      });

    if (page) {
      const start = (page - 1) * this.attachmentPagination.perPage;
      const end = start + this.attachmentPagination.perPage;
      return primary
        ? filteredAttachments.slice(start, end)
        : filteredAttachmentsLabel.slice(start, end);
    }

    return primary ? filteredAttachments : filteredAttachmentsLabel;
  };

  @action
  setAttachmentsPage = (page) => {
    this.attachmentPagination.page = page;
  };

  @action
  fetchLatestPublications = async (limit = 3) => {
    this.loading_latest.status = true;

    try {
      // Uitgelicht is geen serverside veld meer maar een values-entry op het
      // object; haal recente publicaties op en selecteer clientside.
      const recentQuery = {
        ...DEFAULT_QUERY,
        _limit: 100, // Fetch enough to fill gaps
        '_order[publicatiedatum]': 'desc',
      };

      const recentResponse = await app.store.api.publications.search(recentQuery);

      const isFeatured = (item) =>
        item.values?.some(
          (entry) => entry.key === 'featured' && entry.value === 'true'
        );

      const recentItems = (recentResponse.results || []).map((item) => ({
        ...item,
        featured: isFeatured(item),
      }));
      const featuredItems = recentItems.filter((item) => item.featured);

      let finalItems;

      if (featuredItems.length >= limit) {
        // If we have enough featured items, just take the most recent ones
        finalItems = featuredItems.slice(0, limit);
      } else if (featuredItems.length > 0) {
        // If we have some featured items but not enough, add recent non-featured items
        const nonFeaturedItems = recentItems.filter(
          (item) =>
            !item.featured &&
            !featuredItems.some((featured) => featured.id === item.id)
        );

        finalItems = [
          ...featuredItems,
          ...nonFeaturedItems.slice(0, limit - featuredItems.length),
        ];
      } else {
        // Fallback: If no featured items, use most recent
        finalItems = recentItems.slice(0, limit);
      }

      this.setLatestItems(finalItems);
    } catch (e) {
      // Set empty array on error to avoid UI issues
      this.setLatestItems([]);
    } finally {
      this.loading_latest.status = false;
    }
  };

  @computed
  get all_theme_facets() {
    return this.themesFacets;
  }

  @computed
  get search_query() {
    return { ...this.defaultQuery, ...this.aggregationsQuery, ...this.query };
  }

  get aggregations_query() {
    return { ...this.defaultQuery, ...this.aggregationsQuery };
  }

  @computed
  get is_loading() {
    return this.loading.status;
  }

  @computed
  get is_loading_latest() {
    return this.loading_latest.status;
  }

  @computed
  get get_order() {
    return this.query._order;
  }

  @computed
  get get_single() {
    return acMapPublication(this.single);
  }

  @computed
  get all_publications() {
    return this.items?.map((publication) => acMapPublication(publication)) || [];
  }

  @computed
  get latest_publications() {
    return this.latest_items ? toJS(this.latest_items) : [];
  }

  @action
  setItems = (items) => {
    this.items = items;
  };

  @action
  setLatestItems = (items) => {
    this.latest_items = items;
  };

  @action
  setAttachments = (attachments) => {
    this.attachments = attachments;
  };

  @action
  setPagination = (pagination) => {
    this.pagination = pagination;
  };

  @action
  setAttachmentPagination = (pagination) => {
    this.attachmentPagination = pagination;
  };

  @action
  category_checked = (id) => {
    return this.query.category?.includes(id);
  };

  @action
  theme_checked = (id) => {
    return this.query.themes?.includes(id);
  };

  @action
  setQueryDate = (key, value) => {
    if (!this.query.publicatiedatum) {
      this.query.publicatiedatum = {};
    }

    this.setPage(1);
    this.query.publicatiedatum[key] = value;
  };

  @action
  updateQuery = (query) => {
    this.query = { ...this.query, ...query };
  };

  @action
  setSearchQuery = (searchQuery) => {
    this.query._search = searchQuery;
  };

  @action
  setPage = (page) => {
    this.query._page = page;
    this.pagination.page = page;
  };

  @action
  resetSort = () => {
    const newObject = { ...this.query };
    delete newObject._order;
    this.query = newObject;
  };

  @action
  setSort = (key, value) => {
    this.query._order = {};
    this.query._order[key] = value;
  };

  @action
  toggleSearchArrayValue = (key, value) => {
    if (!this.query[key]) {
      this.query[key] = [];
    }

    const index = this.query[key]?.indexOf(value);
    // Remove item if we find it in the array.
    if (index !== -1) {
      this.query[key] = this.query[key].filter((cat) => cat !== value);
      return;
    }

    if (key === 'category') {
      this.setPage(1);
    }

    this.query[key] = [...this.query[key], value];
  };

  @action
  setLoadingStatus = (status) => {
    this.loading.status = status;
  };

  @action
  setPublication = (publication) => {
    this.single = publication;
  };

  @action
  setThemes = (themes) => {
    this.themes = themes;
  };

  @action
  setCategories = (categories) => {
    this.categories = categories;
  };

  @action
  setCategoryFacets = (categoryFacets) => {
    this.categoryFacets = categoryFacets;
  };

  @action
  setThemesFacets = (themesFacets) => {
    this.themesFacets = themesFacets;
  };

  @action
  toggleMobileFilters = () => {
    this.mobileFiltersOpen = !this.mobileFiltersOpen;
  };

  @action
  getSearchPageURL = (params = null) => {
    const urlParams = AcBuildURLSearchParams(params ?? this.query);
    if (!urlParams) {
      return '/zoeken';
    }
    return `/zoeken?${urlParams}`;
  };

  // Vertaalt de UI-query (categorienamen, publicatiedatum-object) naar
  // server-params; /zoeken-URLs en componentwaarden blijven zo ongewijzigd.
  toServerQuery = (query) => {
    const { category, themes, publicatiedatum, ...rest } = query;
    const params = { ...rest };

    if (category?.length) {
      // Serverfilter verwacht (omgevings-specifieke) schema-id's; vertaal de
      // categorienamen via de geladen facet-buckets.
      const ids = category
        .map((name) => this.categories.find((bucket) => bucket.key === name)?.value)
        .filter((id) => id !== undefined);
      if (ids.length) params['@self[schema]'] = ids;
    }

    if (themes?.length) {
      params['thema'] = themes;
    }

    if (publicatiedatum?.gte) params['publicatiedatum[gte]'] = publicatiedatum.gte;
    if (publicatiedatum?.lte) params['publicatiedatum[lte]'] = publicatiedatum.lte;

    return params;
  };

  @action
  fetchPublications = async () => {
    this.loading.status = true;

    app.store.api.publications
      .search(this.toServerQuery(this.search_query))
      .then((response) => {
        this.setCategoryFacets(acNormalizeBuckets(response.facets?._schema));
        this.setThemesFacets(acNormalizeBuckets(response.facets?.thema));

        this.setItems(response.results);
        delete response.results;
        this.setPagination(response);
      })
      .catch((e) => console.error(e))
      .finally(() => {
        this.setLoadingStatus(false);
      });
  };

  @action
  fetchPublication = async (_id) => {
    this.loading.status = true;

    // De embedded attachments-array op de publicatie is deprecated; het
    // attachments-endpoint is de bron en levert size/labels direct mee.
    Promise.all([
      app.store.api.publications.single(_id),
      app.store.api.publications.attachments(_id).catch(() => ({ results: [] })),
    ])
      .then(([response, attachmentsResponse]) => {
        this.setPublication({
          ...response,
          attachments: attachmentsResponse?.results || [],
        });
      })
      .catch((e) => console.error(e))
      .finally(() => {
        this.setLoadingStatus(false);
      });
  };

  @action
  resetPublication = () => {
    this.single = null;
  };

  @action
  fetchAttachments = async (_id) => {
    this.loading.status = true;

    app.store.api.publications
      .attachments(_id)
      .then((response) => {
        this.setAttachments(response.results);
      })
      .catch((e) => console.error(e))
      .finally(() => {
        this.setLoadingStatus(false);
      });
  };

  @action
  resetAttachments = () => {
    this.attachments = null;
  };

  @action
  resetSearchQuery = () => {
    this.query = DEFAULT_SEARCH_QUERY;
  };

  @action
  resetAggregations = () => {
    this.categories = [];
    this.themesFacets = [];
  };

  @action
  fetchAggregations = async () => {
    this.loading.status = true;
    app.store.api.publications
      .searchAggregations({ ...this.aggregations_query, _limit: 0 })
      .then((response) => {
        this.setCategories(acNormalizeBuckets(response.facets?._schema));
        this.setThemes(acNormalizeBuckets(response.facets?.thema));
      })
      .catch((e) => console.error(e))
      .finally(() => {
        this.setLoadingStatus(false);
      });
  };

  @action
  setAttachmentSearch = (search) => {
    this.attachmentSearch = search;
    this.attachmentPagination.page = 1; // Reset to first page when searching
  };
}

export default PublicationsStore;
